import {FilterType, IGameFilter} from './filters/game-filter.interface';
import {GamesMap} from '../games/games.service.interface';
import {OtherFilter} from './filters/other.filter';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {action, computed, observable} from 'mobx';
import {ActiveFilter, IGamesFilterService} from './games-filter.service.interface';
import {ServiceBase} from '../ServiceBase';
import {FILTERS} from './filters-definitions';
import {VendorFilter} from './filters/vendor.filter';
import {CompanyFilter} from './filters/company.filter';
import {GameGroupFilter} from './filters/game-group.filter';
import {GameTypesFilter} from './filters/game-types.filter';
import {Check} from "@sparkware/app-utils";


export class GamesFilterService extends ServiceBase implements IGamesFilterService {

  constructor(services: IServiceFactoryExtended) {
    super(services);

    this._availableGamesFilters = {...FILTERS, other: new OtherFilter( FILTERS)};

    this.reaction(() => {
          return {
            filters: this._currentFilters,
            searchText: this._searchText,
            gamesMap: this.services.games.gamesMap
          }
        },
        (data) => {
          this.filteredGamesMap = this._applyFiltering(data.gamesMap, data.filters, data.searchText);
          if(!Check.isEmpty(data.gamesMap)) {
            this.isFilteredGamesMapInitialized = true;
          }
        });

    this.reaction(() => {
      return {
        arenaName: this.services.routing.currentRoute.data && this.services.routing.currentRoute.data.arenaType,
        fragment: this.services.location.currentUri.hash.value
      }

    }, (routeData) => {
      this._setCurrentFilters(routeData.arenaName, routeData.fragment);
    }, {
      fireImmediately: true
    });
  }

  @observable.ref isFilteredGamesMapInitialized = false;

  @observable.ref private _searchText: string = '';

  _filterByTextDebounceTimeout: number | null = null;

  filterByText(text: string): void {
    if (this._filterByTextDebounceTimeout) {
      clearTimeout(this._filterByTextDebounceTimeout);
    }

    this._filterByTextDebounceTimeout = setTimeout(() => {
      this._setSearchText(text);
    }, 50);
  }

  @action.bound
  private _setSearchText(text: string): void {
    text = text || '';
    text = text.trim().replace(/^\s+/g, '').replace(/\s\s+/g, ' ');
    this._searchText = text; // removeSpecialCharacters(text); // TODO check if we really need this
  }

  private _availableGamesFilters:{[key: string]: IGameFilter};

  @observable.ref private _currentFilters: ActiveFilter[] = [];
  @computed public get currentFilters(): ActiveFilter[] {
    return this._currentFilters.filter(f => !f.isDynamic);
  }

  @action.bound
  private _setCurrentFilters(arenaName: string, fragment: string) {
    let arenaFilters = this._getArenaFilters(arenaName);
    let filtersFromFragment = this._extractFiltersFromFragment(fragment);

    let newFilters = arenaFilters.map(filter => {
      let filterFromFragmentIndex = filtersFromFragment.findIndex(filterId => filterId === filter.id);
      if(0 <= filterFromFragmentIndex) {
        return this._cloneFilter(filter, true);
      } else {
        return this._cloneFilter(filter, false);
      }
    });

    // Load dynamic filters
    filtersFromFragment.forEach(filterFromFragment => {
      if(arenaFilters.filter(f => f.id === filterFromFragment).length === 0) {
        const dynamicFilter = this.services.dynamicFilterFactory.createDynamicFilter(filterFromFragment);

        if(dynamicFilter) {
          this._availableGamesFilters[dynamicFilter.filterID] = dynamicFilter.gameFilter;
          newFilters.push(dynamicFilter.activeFilter);
        }
      }
    });

    this._currentFilters = newFilters;
  }

  @computed
  get isFilterOn(): boolean {
    return this._currentFilters.filter(f => f.on).length > 0;
  }


  @observable.ref filteredGamesMap: GamesMap = {};

  private _applyFiltering(gamesMap: GamesMap, currentActiveFilters: ActiveFilter[], searchText: string): GamesMap {
    return this._applyTextFiltering(this._applyGamesFilters(currentActiveFilters, gamesMap), searchText);
  }

  private _applyGamesFilters(currentActiveFilters: ActiveFilter[], gamesMap: GamesMap): GamesMap {
    if(!currentActiveFilters) {
      return gamesMap;
    }

    let onFilters = currentActiveFilters.filter(f => f.on);

    if(onFilters.length === 0) {
      return gamesMap;
    }

    let onFiltersGroupedByType = {
      [FilterType.categoryFilter]: [] as IGameFilter[],
      [FilterType.featureFilter]: [] as IGameFilter[]
    };

    onFilters.forEach(filter => {
      let filterDefinition = this._availableGamesFilters[filter.id];
      if(filterDefinition) {
        onFiltersGroupedByType[filterDefinition.filterType].push(filterDefinition);
      } else {
        this.services.logger.error(`There is no defined filter with ID ${filter.id}`);
      }
    });

    let result: GamesMap = {};

    Object.values(gamesMap).forEach(gameInfo => {
      let categoriesFilters = onFiltersGroupedByType[FilterType.categoryFilter];
      if((categoriesFilters.length === 0 || this._checkCategories(gameInfo, categoriesFilters, currentActiveFilters))
        && this._checkFeatures(gameInfo, onFiltersGroupedByType[FilterType.featureFilter], currentActiveFilters)) {
        result[gameInfo.gameType] = gameInfo
      }
    });


    return result;
  }

  private _applyTextFiltering(gamesMap: GamesMap, searchText: string): GamesMap {
    if(!searchText) {
      return gamesMap
    }

    let result: GamesMap = {};

    let searchWordsRegExp = searchText.split(' ').map(word => new RegExp(word, 'gi'));

    Object.values(gamesMap).forEach(gameInfo => {
      if(gameInfo.isAllWordsMatch(searchWordsRegExp)) {
        result[gameInfo.gameType] = gameInfo;
      }
    });

    return result;

  }



  private  _checkCategories(game, filters, currentActiveFilters: ActiveFilter[]){
    for(let filter of filters){
      if(filter.applyFilter(game, currentActiveFilters)){
        return true;
      }
    }

    return false;
  }

  private _checkFeatures(game, filters, currentActiveFilters: ActiveFilter[]){
    for(let filter of filters){
      if(!filter.applyFilter(game, currentActiveFilters)){
        return false;
      }
    }

    return true;
  }


  private _getArenaFilters(arenaName: string): ActiveFilter[] {
    let arena = this.services.configuration.data.arenas[arenaName];
    if(!arena) {
      return [];
    }

    let filters = arena.filters || [];

    return filters.filter(({display}) => display !== false).map(filter => {
      return {
        id: filter.id,
        on: false,
        text: filter.text ? this.services.translation.translate(filter.text) : "",
        isDynamic: false
      };
    });
  }

  private _extractFiltersFromFragment(fragment): string[] {

    if(fragment) {
      let params = fragment.split('=');
      if(params[0] === 'filters') {
        return params[1].split(',');
      }
    }

    return [];
  }

  /**
   * change filter to on and if already on - not change him.
   * only one filter can be on.
   * @param filterId
   */
  runFilter(filterId: string) {
    // we should use _getAllFiltersDisabled and not _disableAllFilters because we don't want to refresh the dom yet.
    let disabledFilters = this._getAllFiltersDisabled()

    let newFilters =  disabledFilters.map(filter => {
      if(filter.id === filterId && !filter.on) {
        return this._cloneFilter(filter, !filter.on);
      } else {
        return filter;
      }
    });
    this._doFilterNavigation(newFilters);
  }

  clearFilters(shouldRedirect: boolean = true): void {
    this._disableAllFilters();
    if(shouldRedirect) {
      this._doFilterNavigation(this._currentFilters);
    }
  }

  @action.bound
  _disableAllFilters() {
    this._currentFilters =  this._getAllFiltersDisabled();
  }

  _getAllFiltersDisabled() {
    return this._currentFilters.map(filter => {
      if(filter.on) {
        return this._cloneFilter(filter, !filter.on);
      } else {
        return filter;
      }
    });
  }

  toggleFilter(filterId: string) {
    let newFilters =  this._currentFilters.map(filter => {
      if(filter.id === filterId) {
        return this._cloneFilter(filter, !filter.on);
      } else {
        return filter;
      }
    });
    this._doFilterNavigation(newFilters);
  }

  applyMultipleFilters(filterIds: string[]) {
    let newFilters = this._currentFilters.map(filter => {
      if(0 <= filterIds.findIndex(filterId => filter.id === filterId)) {
        return this._cloneFilter(filter,true)
      } else {
        return this._cloneFilter(filter,false);
      }
    });

    this._doFilterNavigation(newFilters);
  }

  private _doFilterNavigation(newFilters: ActiveFilter[]): void {
    let onFilters = newFilters.filter(filter => filter.on)
      .map(filter => filter.id);

    this.services.window.scrollTo(0, 0);

    if(onFilters.length > 0) {
      this.services.location.push('#filters=' + onFilters.join(','));
    } else {
      this.services.location.push(this.services.location.currentUri.pathName.toString());
    }
  }

  private _cloneFilter(originalFilter: ActiveFilter, checked: boolean): ActiveFilter {
    return {...originalFilter,  on: checked };
  }

  public tryCreateFilter(filterType: string, filterValue: any): IGameFilter | null {
    switch (filterType) {

      case "gameCompany":
        return new CompanyFilter(filterValue);

      case "gameVendor":
        return new VendorFilter(filterValue);

      case "gameGroup":
        return new GameGroupFilter(filterValue);

      case "gameTypes":
        return new GameTypesFilter(filterValue);

      default:
        return null;
    }
  }

  public supportFiltersV2(): boolean {
    if(this.services.routing.currentRoute.data && this.services.routing.currentRoute.data.arenaType){
      const arena = this.services.configuration.data.arenas[this.services.routing.currentRoute.data.arenaType];

      if(!arena) {
        return false;
      }

      return !!arena.filters_v2;
    }

    return false;
  }



}
